<template>
  <div class="container checkoutPage">
    <div class="has-text-centered gant-text mb-5">
      <h1 class="title gant-text">Select</h1>
      <h2 class="subtitle gant-text">
        Selecting a booking class for this flight
      </h2>
    </div>

    <div class="boxWithBorder mb-3" style="border-color: #a4393a">
      <div class="p-4">
        <div class="columns">
          <div class="column">
            <h2 class="is-size-4 ml mb-5 has-text-weight-bold">
              Selected Itinerary:
            </h2>

            <div
              v-if="loading"
              class="is-size-4 has-font-weight-bold has-text-left"
              data-test="show_loading"
            >
              <div class="is-size-5">Getting latest fare details...</div>
              <b-skeleton
                size="is-large"
                width="20%"
                :animated="true"
              ></b-skeleton>
              <b-skeleton
                size="is-large"
                width="35%"
                :animated="true"
              ></b-skeleton>
              <b-skeleton
                size="is-large"
                width="50%"
                :animated="true"
              ></b-skeleton>
            </div>

            <div v-else>
              <div class="columns is-mobile">
                <!-- AIRLINE LOGO -->
                <div class="column is-narrow has-text-centered wrap">
                  <div class="mt-1">
                    <img
                      width="30"
                      :src="$helpers.airlineLogo(solution.airlineCode, 30, 30)"
                    />
                  </div>
                </div>
                <!-- FROM-TO -->
                <div class="column">
                  <div class="has-text-weight-semibold">
                    {{ $helpers.airlineName(solution.airlineCode) }}
                  </div>

                  <div class="columns is-mobile">
                    <JourneySummaryAlt
                      v-for="(journey, journeyIndex) in solution.journeys"
                      :key="journeyIndex"
                      :journey="journey"
                      :index="journeyIndex"
                      class="column is-narrow"
                    />
                  </div>
                </div>
              </div>

              <NDCFareOption
                v-if="priceData"
                :solution="solution"
                :offer="priceData.pricedOffer"
              />
            </div>

            <b-notification
              v-if="failed"
              type="is-danger"
              :closable="false"
              position="is-top-right"
              has-icon
              icon="clock"
              icon-size="is-small"
            >
              <div class="is-size-5 mt-2">
                Sorry, we were unable to retrieve fare details for this
                selection. Please try again or choose another flight.
              </div>
            </b-notification>
          </div>

          <div class="column">
            <b-message
              title="Airline Direct (NDC) Fare"
              type="is-success"
              aria-close-label="Close message"
            >
              <ul>
                <li>
                  This fare is a direct airline fare and may contain special
                  pricing.
                </li>
                <li>
                  NDC fares do not allow car rentals or hotel stays to be added 
                  to the reservation at the time of booking. Once you've finalized 
                  the reservation, you may book a car and/or hotel for this trip in 
                  two ways: 1) add to the existing reservation through 'My Trips' 
                  or 2) complete a new and separate reservation. There is no 
                  additional charge(s) for booking car and/or hotel utilizing either 
                  of these options.
                </li>
                <li>
                  <strong>PLEASE NOTE:</strong> Gant Travel does not charge an NDC service surcharge for NDC reservations booked through the online tool.  However, should an NDC reservation that originates online require the intervention or assistance of a Gant agent after booking (i.e.: exchanges, itinerary changes, etc.), the NDC service surcharge of $24.00 will be charged at that time. This fee is in addition to your contracted agent-assist fees.
                </li>
              </ul>
            </b-message>

            <b-notification
              v-if="priceData"
              :type="countdown > 0 ? 'is-warning' : 'is-danger'"
              :closable="false"
              position="is-top-right"
              has-icon
              icon="clock"
              icon-size="is-small"
            >
              <div v-if="countdown > 0" class="is-size-5 mt-2">
                Hurry! This fare expires in {{ minutes }} minutes
                {{ seconds }} seconds.
              </div>
              <div v-else class="is-size-5 mt-2">
                Sorry, this fare has expired.
              </div>
            </b-notification>
          </div>
        </div>

        <div v-if="priceData && otherOffers.length > 0">
          <h2 class="is-size-4 ml-3 mb-5">
              Other Options:
            </h2>
          <div  class="columns" style="overflow-x:scroll">
            <div
              v-for="(offer, index) in priceData.otherOffers"
              :key="index"
              class="column"
            >
              <NDCFareOption :solution="solution" :offer="offer" />
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import NDCFareOption from "./NDCFareOption.vue";
import JourneySummaryAlt from "./JourneySummaryAlt";
import moment from "moment";

export default {
  name: "OfferPricePage",
  //mixins: [bookingMixin],
  props: {
    //solution: Object,
  },
  components: {
    NDCFareOption,
    JourneySummaryAlt,
  },
  data() {
    return {
      priceData: null,
      countdown: null,
      loading: false,
      failed: false,
      updateKey: 0,
    };
  },
  watch: {
    updateKey: {
      handler: function(val, oldVal) {
        if (val) {
          this.getOfferPrice();
        }
      },
    },
  },
  methods: {
    countdownTimer() {
      if (this.countdown > 0) {
        setTimeout(() => {
          this.countdown -= 1;
          this.countdownTimer();
        }, 1000);
      }
    },
    async getOfferPrice() {
      this.loading = true;

      let body = {
        includeUpsells: true,
        ownerCode: this.selectedOffer.ownerCode,
        responseId: this.selectedOffer.responseId,
        offerId: this.selectedOffer.offerId,
        items: this.selectedOffer.items,
      };

      let result = await this.$app.huddleLabsServerCall(
        "amadeus/travel/offer-price",
        body
      );

      this.loading = false;

      if (result.data) {
        this.priceData = result.data;

        //update the SelectedOffer using values returned by call to travel/offer-price
        let selectedOffer = this.priceData.pricedOffer;

        //filter out optional (upsell) items
        selectedOffer.items = this.priceData.pricedOffer.items.filter(
          (item) => item.required === true
        );

        let payload = {
          solution: this.solution,
          offer: selectedOffer,
        };

        this.$store.dispatch("bookTravel/setNdcBooking", payload);

        let expireTime = moment.utc(this.priceData.pricedOffer.expireTime);
        this.countdown =
          expireTime > moment.utc()
            ? Math.abs(moment().diff(expireTime, "seconds"))
            : 0;

        this.countdownTimer();
      } else {
        this.failed = true;
      }
    },
  },

  computed: {
    fareDescription() {
      return this.selectedOffer?.bookingClass;
    },
    otherOffers() {
      return this.priceData?.otherOffers || [];
    },
    solution() {
      return this.$store.state.bookTravel.ndcBooking.flight;
    },
    selectedOffer() {
      return this.$store.state.bookTravel.ndcBooking.selectedOffer;
    },
    minutes() {
      return Math.floor(this.countdown / 60);
    },
    seconds() {
      return this.countdown % 60;
    },
  },
  async created() {
    if (this.selectedOffer) {
      this.getOfferPrice();
    }
  },
};
</script>

<style lang="scss" scoped></style>
